import {
  ApiError,
  type AuthUser,
  type CatalogApplication,
  type CatalogApplicationDetail,
  type CatalogBundle,
  type CatalogCategory,
  type CatalogCustomerState,
  type CatalogDelivery,
  type CatalogPreference,
  type CatalogItem,
  type CompliancePack,
  type ContractDetail,
  type ContractSummary,
  type DashboardMetrics,
  type Entitlement,
  type Invoice,
  type Organization,
  type Plan,
  type Product,
  type QaResult,
  type ReviewItem,
  type Subscription,
  type UsageSummary,
  type Clause,
  type Obligation,
  type IngestResult,
  type AdminApplicationSummary,
  type AdminApplicationDetail,
  type AdminBundleSummary,
  type AdminBundleDetail,
  type AdminReference,
  type AdminAuditEvent,
  type AdminEntitlementMapping,
  type AdminPriceMapping,
  type CatalogLifecycleStatus,
  type AppSumoTierSummary,
} from "./types";

export interface StudioClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

export class StudioClient {
  private baseUrl: string;
  private getToken: () => string | null;
  private onUnauthorized: (() => void) | undefined;

  constructor(opts: StudioClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.getToken = opts.getToken ?? (() => null);
    this.onUnauthorized = opts.onUnauthorized;
  }

  private async request<T>(method: string, path: string, body?: unknown, extraHeaders?: Record<string, string>): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json", ...extraHeaders };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, init);
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach the server. Check your connection.");
    }
    if (res.status === 401 && this.onUnauthorized) this.onUnauthorized();
    let data: unknown = null;
    const text = await res.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }
    }
    if (!res.ok) {
      const err = (data ?? {}) as { error?: string; message?: string };
      throw new ApiError(res.status, err.error ?? "error", err.message ?? `Request failed (${res.status}).`);
    }
    return data as T;
  }

  // ---- Auth ----
  register(input: { email: string; password: string; displayName?: string }) {
    return this.request<{ token: string; user: AuthUser; verification: { sent: boolean; link?: string } }>(
      "POST",
      "/auth/register",
      input,
    );
  }
  login(input: { email: string; password: string }) {
    return this.request<{ token: string; user: AuthUser }>("POST", "/auth/login", input);
  }
  logout() {
    return this.request<{ ok: true }>("POST", "/auth/logout");
  }
  me() {
    return this.request<{ user: AuthUser; organizations: Organization[] }>("GET", "/auth/me");
  }
  verifyEmail(token: string) {
    return this.request<{ ok: true; email: string }>("POST", "/auth/verify", { token });
  }
  resendVerification() {
    return this.request<{ ok: true; sent?: boolean; link?: string; alreadyVerified?: boolean }>(
      "POST",
      "/auth/resend-verification",
    );
  }
  forgotPassword(email: string) {
    return this.request<{ ok: true; link?: string }>("POST", "/auth/forgot-password", { email });
  }
  resetPassword(token: string, password: string) {
    return this.request<{ ok: true; email: string }>("POST", "/auth/reset-password", { token, password });
  }

  // ---- Org ----
  // When withTrial is set, the org + membership + 14-day trial (subscription +
  // entitlements) are created atomically in one request. `app` names the catalog
  // app to trial (e.g. "app-policy"); omitted, signup trials Contract AI. An app
  // that is not self-service trialable is rejected, never substituted.
  createOrganization(name: string, opts: { withTrial?: boolean; app?: string } = {}) {
    return this.request<{
      id: string;
      name: string;
      slug: string;
      role: string;
      trial?: {
        subscriptionId: string;
        status: string;
        planId: string;
        productKey: string;
        entitlements: string[];
        trialDays: number;
      };
    }>("POST", "/organizations", {
      name,
      ...(opts.withTrial ? { withTrial: true } : {}),
      ...(opts.app ? { app: opts.app } : {}),
    });
  }
  // Start a self-service 14-day Contract AI trial for the organization.
  startTrial(orgId: string) {
    return this.request<{ subscriptionId: string; status: string; entitlements?: string[]; trialDays?: number; reused?: boolean }>(
      "POST",
      `/organizations/${orgId}/trial`,
    );
  }
  // Start a self-service 14-day trial of a specific app (productKey e.g.
  // "app-proposal"). Idempotent and reset-proof server-side.
  startAppTrial(orgId: string, productKey: string) {
    return this.request<{
      subscriptionId: string;
      planId: string;
      status: string;
      entitlements?: string[];
      trialDays?: number;
      trialEndsAt?: string | null;
      reused?: boolean;
    }>("POST", `/organizations/${orgId}/apps/${productKey}/trial`);
  }
  members(orgId: string) {
    return this.request<{ members: { id: string; email: string; displayName: string; role: string }[] }>(
      "GET",
      `/organizations/${orgId}/members`,
    );
  }
  setMemberRole(orgId: string, userId: string, role: string) {
    return this.request<{ ok: true; userId: string; role: string }>(
      "PATCH",
      `/organizations/${orgId}/members/${userId}`,
      { role },
    );
  }
  // ---- Invitations ----
  createInvitation(orgId: string, input: { email: string; role?: string }) {
    return this.request<{ id: string; email: string; role: string; sent: boolean; link?: string }>(
      "POST",
      `/organizations/${orgId}/invitations`,
      input,
    );
  }
  invitations(orgId: string) {
    return this.request<{ invitations: { id: string; email: string; role: string; status: string; created_at: string; expires_at: string }[] }>(
      "GET",
      `/organizations/${orgId}/invitations`,
    );
  }
  revokeInvitation(orgId: string, inviteId: string) {
    return this.request<{ ok: true }>("POST", `/organizations/${orgId}/invitations/${inviteId}/revoke`);
  }
  lookupInvitation(token: string) {
    return this.request<{ email: string; role: string; orgName: string }>(
      "GET",
      `/invitations/lookup?token=${encodeURIComponent(token)}`,
    );
  }
  acceptInvitation(token: string) {
    return this.request<{ ok: true; organizationId: string; role: string }>("POST", "/invitations/accept", { token });
  }

  // ---- Catalog ----
  products() {
    return this.request<{ products: Product[] }>("GET", "/catalog/products");
  }
  plans(productKey = "contract-ai") {
    return this.request<{ plans: Plan[] }>("GET", `/catalog/plans?product=${encodeURIComponent(productKey)}`);
  }
  launchCatalog() {
    return this.request<{ items: CatalogItem[] }>("GET", "/catalog/launch");
  }

  // ---- Studio Marketplace (ODS-CATALOG-001) ----
  catalogApplications(
    params: {
      q?: string;
      category?: string;
      status?: string;
      delivery?: CatalogDelivery;
      sort?: "name" | "sort";
      page?: number;
      pageSize?: number;
    } = {},
  ) {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.category) qs.set("category", params.category);
    if (params.status) qs.set("status", params.status);
    if (params.delivery) qs.set("delivery", params.delivery);
    if (params.sort) qs.set("sort", params.sort);
    if (params.page) qs.set("page", String(params.page));
    if (params.pageSize) qs.set("pageSize", String(params.pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return this.request<{ applications: CatalogApplication[]; page: number; pageSize: number; total: number }>(
      "GET",
      `/catalog/applications${suffix}`,
    );
  }
  catalogApplication(slug: string) {
    return this.request<CatalogApplicationDetail>("GET", `/catalog/applications/${encodeURIComponent(slug)}`);
  }
  catalogCategories() {
    return this.request<{ categories: CatalogCategory[] }>("GET", "/catalog/categories");
  }
  catalogBundles() {
    return this.request<{ bundles: CatalogBundle[] }>("GET", "/catalog/bundles");
  }
  // Server-authoritative customer state for every catalog app for an org.
  catalogState(orgId: string) {
    return this.request<{ states: CatalogCustomerState[] }>("GET", `/organizations/${orgId}/catalog/state`);
  }
  catalogAppState(orgId: string, slug: string) {
    return this.request<CatalogCustomerState>("GET", `/organizations/${orgId}/catalog/applications/${encodeURIComponent(slug)}/state`);
  }
  // Per (org, user) preferences: pinned apps + recently-used (open telemetry).
  catalogPreferences(orgId: string) {
    return this.request<{ preferences: CatalogPreference[] }>("GET", `/organizations/${orgId}/catalog/preferences`);
  }
  // Pin/unpin are idempotent server-side.
  pinCatalogApp(orgId: string, slug: string) {
    return this.request<{ slug: string; pinned: boolean }>("PUT", `/organizations/${orgId}/catalog/applications/${encodeURIComponent(slug)}/pin`);
  }
  unpinCatalogApp(orgId: string, slug: string) {
    return this.request<{ slug: string; pinned: boolean }>("DELETE", `/organizations/${orgId}/catalog/applications/${encodeURIComponent(slug)}/pin`);
  }
  // Record a launch (advisory: drives recently-used + telemetry, never gates access).
  recordCatalogOpen(orgId: string, slug: string) {
    return this.request<{ slug: string; recorded: boolean }>("POST", `/organizations/${orgId}/catalog/applications/${encodeURIComponent(slug)}/open`);
  }
  billingConfig() {
    return this.request<{ liveCheckout: boolean }>("GET", "/billing/config");
  }

  // ---- Catalog administration (ODS-CATALOG-001 P4; platform_admin only) ----
  // One-time bootstrap: grant the first platform_admin using a server secret.
  platformBootstrap(secret: string, email: string) {
    return this.request<{ ok: true; email: string; platformRole: string }>("POST", "/platform/bootstrap", { email }, { "x-bootstrap-secret": secret });
  }
  adminReference() {
    return this.request<AdminReference>("GET", "/platform/reference");
  }
  adminApplications() {
    return this.request<{ applications: AdminApplicationSummary[] }>("GET", "/platform/applications");
  }
  adminApplication(slug: string) {
    return this.request<AdminApplicationDetail>("GET", `/platform/applications/${encodeURIComponent(slug)}`);
  }
  createAdminApplication(input: { slug: string; name: string; primaryEntitlement: string; tagline?: string; description?: string; icon?: string; categoryId?: string | null; planKey?: string | null; sortOrder?: number }) {
    return this.request<{ ok: true; id: string; slug: string }>("POST", "/platform/applications", input);
  }
  updateAdminApplication(slug: string, input: Partial<{ name: string; tagline: string; description: string; icon: string; categoryId: string | null; planKey: string | null; primaryEntitlement: string; sortOrder: number }>) {
    return this.request<{ ok: true }>("PATCH", `/platform/applications/${encodeURIComponent(slug)}`, input);
  }
  setAdminApplicationLifecycle(slug: string, input: { status: CatalogLifecycleStatus; maintenanceMessage?: string | null; deprecationNotice?: string | null }) {
    return this.request<{ ok: true; from: string; to: string }>("POST", `/platform/applications/${encodeURIComponent(slug)}/lifecycle`, input);
  }
  setAdminApplicationEntitlements(slug: string, entitlements: AdminEntitlementMapping[]) {
    return this.request<{ ok: true; count: number }>("PUT", `/platform/applications/${encodeURIComponent(slug)}/entitlements`, { entitlements });
  }
  setAdminApplicationPrices(slug: string, prices: AdminPriceMapping[]) {
    return this.request<{ ok: true; count: number }>("PUT", `/platform/applications/${encodeURIComponent(slug)}/prices`, { prices });
  }
  setAdminApplicationTrialPolicy(slug: string, input: { enabled: boolean; trialDays: number; planKey?: string | null }) {
    return this.request<{ ok: true }>("PUT", `/platform/applications/${encodeURIComponent(slug)}/trial-policy`, input);
  }
  setAdminApplicationNavigation(slug: string, input: { route?: string | null; navLabel?: string; navSection?: string; sortOrder?: number }) {
    return this.request<{ ok: true }>("PUT", `/platform/applications/${encodeURIComponent(slug)}/navigation`, input);
  }
  setAdminApplicationTrust(slug: string, input: { trustCenterUrl?: string | null; dataHandling?: string; certifications?: string[]; securityNotes?: string }) {
    return this.request<{ ok: true }>("PUT", `/platform/applications/${encodeURIComponent(slug)}/trust`, input);
  }
  setAdminApplicationRelease(slug: string, input: { currentVersion?: string; releaseNotesUrl?: string | null; releasedAt?: string | null; knownLimitations?: string; changelog?: unknown[] }) {
    return this.request<{ ok: true }>("PUT", `/platform/applications/${encodeURIComponent(slug)}/release`, input);
  }
  addAdminApplicationVersion(slug: string, input: { version: string; notes?: string; isCurrent?: boolean; releasedAt?: string }) {
    return this.request<{ ok: true; version: string }>("POST", `/platform/applications/${encodeURIComponent(slug)}/versions`, input);
  }
  adminBundles() {
    return this.request<{ bundles: AdminBundleSummary[] }>("GET", "/platform/bundles");
  }
  adminBundle(slug: string) {
    return this.request<AdminBundleDetail>("GET", `/platform/bundles/${encodeURIComponent(slug)}`);
  }
  createAdminBundle(input: { slug: string; name: string; description?: string; kind?: string; planKey?: string | null; primaryEntitlement?: string; sortOrder?: number }) {
    return this.request<{ ok: true; id: string; slug: string }>("POST", "/platform/bundles", input);
  }
  updateAdminBundle(slug: string, input: Partial<{ name: string; description: string; kind: string; planKey: string | null; primaryEntitlement: string; sortOrder: number }>) {
    return this.request<{ ok: true }>("PATCH", `/platform/bundles/${encodeURIComponent(slug)}`, input);
  }
  setAdminBundleLifecycle(slug: string, status: CatalogLifecycleStatus) {
    return this.request<{ ok: true; from: string; to: string }>("POST", `/platform/bundles/${encodeURIComponent(slug)}/lifecycle`, { status });
  }
  setAdminBundleApplications(slug: string, slugs: string[]) {
    return this.request<{ ok: true; count: number }>("PUT", `/platform/bundles/${encodeURIComponent(slug)}/applications`, { slugs });
  }
  setAdminBundlePrices(slug: string, prices: AdminPriceMapping[]) {
    return this.request<{ ok: true; count: number }>("PUT", `/platform/bundles/${encodeURIComponent(slug)}/prices`, { prices });
  }
  adminAudit(limit = 50) {
    return this.request<{ events: AdminAuditEvent[] }>("GET", `/platform/audit?limit=${limit}`);
  }

  // ---- Billing ----
  // Live Stripe checkout — returns a Stripe-hosted Checkout URL to redirect to.
  createStripeCheckout(orgId: string, input: { sku: string; interval: "month" | "year" }) {
    return this.request<{ url: string; checkoutSessionId: string; provider: string }>(
      "POST",
      `/organizations/${orgId}/billing/stripe/checkout`,
      input,
    );
  }
  openBillingPortal(orgId: string) {
    return this.request<{ url: string }>("POST", `/organizations/${orgId}/billing/stripe/portal`);
  }
  createCheckoutSession(orgId: string, input: { planId: string; priceId: string; seats: number }) {
    return this.request<{ checkoutSessionId: string; hostedCheckoutUrl: string; provider: string }>(
      "POST",
      `/organizations/${orgId}/billing/checkout-session`,
      input,
    );
  }
  completeCheckout(orgId: string, sessionId: string) {
    return this.request<{ subscriptionId: string; status: string; entitlements: string[] }>(
      "POST",
      `/organizations/${orgId}/billing/checkout-session/${sessionId}/complete`,
    );
  }
  subscription(orgId: string) {
    return this.request<{ subscription: Subscription | null }>("GET", `/organizations/${orgId}/billing/subscription`);
  }
  updateSeats(orgId: string, subId: string, seats: number) {
    return this.request<{ ok: true; seats: number }>("POST", `/organizations/${orgId}/billing/subscription/${subId}/seats`, { seats });
  }
  changePlan(orgId: string, subId: string, input: { planId: string; priceId: string }) {
    return this.request<{ ok: true }>("POST", `/organizations/${orgId}/billing/subscription/${subId}/change-plan`, input);
  }
  cancelSubscription(orgId: string, subId: string) {
    return this.request<{ ok: true; cancelAtPeriodEnd: boolean }>("POST", `/organizations/${orgId}/billing/subscription/${subId}/cancel`);
  }
  resumeSubscription(orgId: string, subId: string) {
    return this.request<{ ok: true; cancelAtPeriodEnd: boolean }>("POST", `/organizations/${orgId}/billing/subscription/${subId}/resume`);
  }
  invoices(orgId: string) {
    return this.request<{ invoices: Invoice[] }>("GET", `/organizations/${orgId}/billing/invoices`);
  }
  usage(orgId: string) {
    return this.request<{ usage: UsageSummary[] }>("GET", `/organizations/${orgId}/billing/usage`);
  }
  entitlements(orgId: string) {
    return this.request<{ entitlements: Entitlement[] }>("GET", `/organizations/${orgId}/entitlements`);
  }

  // ---- AppSumo (lifetime-deal redemption) ----
  // Redeem one AppSumo code onto an organization the caller administers. Codes
  // stack: call once per code and the tier upgrades with the total count.
  redeemAppSumo(input: { code: string; organizationId: string }) {
    return this.request<AppSumoTierSummary & { alreadyRedeemed?: boolean }>("POST", "/appsumo/redeem", input);
  }
  appsumoStatus(orgId: string) {
    return this.request<{
      redeemedCodes: number;
      maxCodes: number;
      tierCodes: number;
      seats: number | null;
      monthlyPages: number | null;
    }>("GET", `/organizations/${orgId}/appsumo`);
  }

  // ---- Contract AI ----
  // Real ingestion: uploads a file (multipart) or raw text and runs extraction.
  async uploadContract(orgId: string, input: File | { text: string; filename?: string }): Promise<IngestResult> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const init: RequestInit = { method: "POST", headers };
    if (input instanceof File) {
      const form = new FormData();
      form.append("file", input);
      init.body = form;
    } else {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(input);
    }
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}/organizations/${orgId}/contract-ai/contracts`, init);
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach the server. Check your connection.");
    }
    if (res.status === 401 && this.onUnauthorized) this.onUnauthorized();
    const textBody = await res.text();
    let data: unknown = null;
    if (textBody) {
      try {
        data = JSON.parse(textBody);
      } catch {
        data = null;
      }
    }
    if (!res.ok) {
      const err = (data ?? {}) as { error?: string; message?: string };
      throw new ApiError(res.status, err.error ?? "error", err.message ?? `Upload failed (${res.status}).`);
    }
    return data as IngestResult;
  }
  dashboard(orgId: string) {
    return this.request<{ metrics: DashboardMetrics; recentUploads: ContractSummary[] }>(
      "GET",
      `/organizations/${orgId}/contract-ai/dashboard`,
    );
  }
  contracts(orgId: string, params: { q?: string; status?: string; risk?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.status) qs.set("status", params.status);
    if (params.risk) qs.set("risk", params.risk);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return this.request<{ contracts: ContractSummary[] }>("GET", `/organizations/${orgId}/contract-ai/contracts${suffix}`);
  }
  contract(orgId: string, contractId: string) {
    return this.request<ContractDetail>("GET", `/organizations/${orgId}/contract-ai/contracts/${contractId}`);
  }
  reviewQueue(orgId: string) {
    return this.request<{ items: ReviewItem[] }>("GET", `/organizations/${orgId}/contract-ai/review-queue`);
  }
  resolveReview(orgId: string, itemId: string, action: "accept" | "correct" | "reject", correctedValue?: string) {
    return this.request<{ ok: true; status: string }>(
      "POST",
      `/organizations/${orgId}/contract-ai/review-queue/${itemId}`,
      { action, correctedValue },
    );
  }
  obligations(orgId: string) {
    return this.request<{ obligations: Obligation[] }>("GET", `/organizations/${orgId}/contract-ai/obligations`);
  }
  clauses(orgId: string) {
    return this.request<{ clauses: (Clause & { contractId: string; contractTitle: string; citationLocator: string | null })[] }>(
      "GET",
      `/organizations/${orgId}/contract-ai/clauses`,
    );
  }
  compliance(orgId: string) {
    return this.request<{ packs: CompliancePack[]; flowDownAlerts: { id: string; contractTitle: string; clauseType: string; riskLevel: string; finding: string }[] }>(
      "GET",
      `/organizations/${orgId}/contract-ai/compliance`,
    );
  }
  askContracts(orgId: string, question: string) {
    return this.request<QaResult>("POST", `/organizations/${orgId}/contract-ai/qa`, { question });
  }
}
